import { useEffect, useState } from 'react';
import { User } from 'services/auth/auth.types';
import { useGetUserProfileMutation } from 'services/user/user.endpoint';
import moment from 'moment';
import { Project } from 'services/person/person.types';
import { Appointment, CreateAppointmentPayload } from 'services/appointment/appointment.types';
import { useCreateAppointmentMutation, useGetAllAppointmentsMutation } from 'services/appointment/appointment.endpoint';
import { useApiToast } from 'components/Toaster';
export default function useAppointmentsVM() {
    const [me, setMe] = useState<User>({} as User);
    const [appointments, setAppointments] = useState<Array<Appointment>>([]);
    const [project, setProject] = useState<Project>({} as Project);
    const [isComponentLoading, setIsComponentLoading] = useState<boolean>(true);
    const [isUserCreateAppointmentModalOpen, setIsUserCreateAppointmentModalOpen] = useState<boolean>(false);
    const { showErrorToast, showApiToast } = useApiToast();

    function formatDate(date: string) {
        return moment(date).format('D MMMM YYYY à HH:mm');
    }

    const handleModalClose = () => {
        setIsUserCreateAppointmentModalOpen(false);
    };

    const [getMe, { isLoading: isMeLoading, isError: isMeError, isSuccess: isMeSuccess, error: meError, data: meResponse }] =
        useGetUserProfileMutation();

    const [
        createAppointment,
        {
            isLoading: isCreateAppointmentLoading,
            isError: isCreateAppointmentError,
            isSuccess: isCreateAppointmentSuccess,
            error: createAppointmentError,
            data: createAppointmentData
        }
    ] = useCreateAppointmentMutation();

    const [
        getAllAppointments,
        {
            isLoading: isAllAppointmentsLoading,
            isError: isAllAppointmentsError,
            isSuccess: isAllAppointmentsSuccess,
            error: AllAppointmentsError,
            data: AllAppointmentsResponse
        }
    ] = useGetAllAppointmentsMutation();

    useEffect(() => {
        getMe(undefined);
    }, [getMe]);

    const handleCreateAppointment = async (values: CreateAppointmentPayload) => {
        const appointment = { ...values, project_id: String(me?.projects?.[0]?.id) };
        await createAppointment(appointment);
        setIsUserCreateAppointmentModalOpen(false);
    };

    useEffect(() => {
        if (isMeLoading) return;
        if (isMeError && meError) {
            showErrorToast(meError, {
                message: 'Erreur lors de la récupération du profil'
            });
            return;
        }
        if (isMeSuccess && meResponse) {
            setMe(meResponse.data);
        }
    }, [isMeLoading, isMeError, isMeSuccess, meError, meResponse, showErrorToast]);

    useEffect(() => {
        if (me?.projects?.[0]?.id) {
            setProject(me.projects[0]);
            getAllAppointments({
                project_id: String(me.projects[0].id)
            });
        }
    }, [me, getAllAppointments]);

    useEffect(() => {
        if (isAllAppointmentsLoading) return;
        if (isAllAppointmentsError && AllAppointmentsError) {
            showErrorToast(AllAppointmentsError, {
                message: 'Erreur de chargement des demandes du projet.'
            });
            return;
        }
        if (isAllAppointmentsSuccess && AllAppointmentsResponse) {
            setIsComponentLoading(false);
            setAppointments(
                [...AllAppointmentsResponse.data].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
            );
        }
    }, [
        isAllAppointmentsLoading,
        isAllAppointmentsError,
        isAllAppointmentsSuccess,
        AllAppointmentsError,
        AllAppointmentsResponse,
        showErrorToast
    ]);

    useEffect(() => {
        if (isCreateAppointmentLoading) return;
        if (isCreateAppointmentError && createAppointmentError) {
            showErrorToast(createAppointmentError, {
                message: 'Erreur lors de la création du rendez-vous'
            });
        }
        if (isCreateAppointmentSuccess && createAppointmentData) {
            showApiToast(createAppointmentData, {
                successMessage: 'Rendez-vous créé avec succès',
                errorMessage: 'Erreur lors de la création du rendez-vous'
            });
        }
    }, [
        isCreateAppointmentLoading,
        isCreateAppointmentError,
        isCreateAppointmentSuccess,
        createAppointmentError,
        createAppointmentData,
        showApiToast,
        showErrorToast
    ]);

    return {
        appointments,
        me,
        formatDate,
        project,
        isApiRequestLoading: isAllAppointmentsLoading,
        isComponentLoading,
        isUserCreateAppointmentModalOpen,
        handleModalClose,
        handleCreateAppointment,
        setIsUserCreateAppointmentModalOpen
    };
}
